import { consume } from "@lit/context";
import {
    type DocumentDTO,
    type DocumentTagDTO,
    fileIcon,
    formatDate,
    formatMonth,
    FormStatus,
    formStatusColor,
    formStatusLabel,
    getRange,
    toDateString,
} from "@pentacode/core";
import "@pentacode/ui/components";
import { css, html, LitElement } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import "../../components/documents/document-dialog";
import type { DocumentDialog } from "../../components/documents/document-dialog";
import { type EnabledFeatures, enabledFeatures } from "../../contexts/features-context";
import { app } from "../../init";
import { singleton } from "../../lib/singleton";
import { readFileAsDataURL, selectFile } from "../../lib/util";
import { routeProperty, Routing } from "../../mixins/routing";
import { StateMixin } from "../../mixins/state";
import { shared } from "../../styles";
import { alert } from "../alert-dialog";
import "../scroller";

@customElement("ptc-staff-documents")
export class Documents extends Routing(StateMixin(LitElement)) {
    routePattern = /^(home|documents)/;

    @property({ reflect: true, type: Boolean })
    expanded = false;

    @routeProperty({ param: "openDoc", type: Number })
    private _openDoc: number;

    @state()
    private _loading = false;

    @state()
    private _documents: DocumentDTO[] = [];

    @state()
    private _months: {
        date: string;
        documents: DocumentDTO[];
    }[];

    @state()
    private _selectedTag: DocumentTagDTO | null = null;

    @state()
    private _availableTags: DocumentTagDTO[] = [];

    @singleton("ptc-document-dialog")
    private _documentDialog: DocumentDialog;

    @consume({ context: enabledFeatures, subscribe: true })
    private _enabledFeatures?: EnabledFeatures;

    refresh() {
        return this._load();
    }

    async handleRoute() {
        await this._load();

        if (this._openDoc) {
            const document = this._documents.find((d) => d.id === this._openDoc);
            if (document) {
                await this._openDocument(document);
            }
            this.go(null, { openDoc: undefined });
        }
    }

    async connectedCallback() {
        super.connectedCallback();
        await app.loaded;
        if (app.account) {
            void this._load();
        }
    }

    private async _load() {
        if (this._loading) return;

        if (!app.profile) {
            return;
        }

        this._loading = true;

        try {
            const documents = await app.api.getDocuments({ employeeId: app.profile.id });

            this._documents = documents
                .filter((d) => d.url || d.form?.status === FormStatus.InProgress)
                .sort((a, b) => (a.date < b.date ? -1 : 1));

            const months: { date: string; documents: DocumentDTO[] }[] = [];

            for (const document of this._documents) {
                const date = getRange(document.date, "month").from;
                if (date === months[0]?.date) {
                    months[0].documents.push(document);
                } else {
                    months.unshift({ date, documents: [document] });
                }
            }

            this._months = months;
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }

        await this._loadAvailableTags();

        this._loading = false;
    }

    private async _loadAvailableTags() {
        try {
            this._availableTags = await app.api.getDocumentTagsForCompany();
        } catch (e) {
            console.warn("Failed to load document tags:", e);
            this._availableTags = [];
        }
    }

    private async _uploadFile(file: File) {
        if (!app.profile) {
            return;
        }

        try {
            const content = await readFileAsDataURL(file);
            await app.api.uploadWorkerDocument({
                name: file.name,
                date: toDateString(new Date()),
                comment: "",
                content,
            });
            await this._load();
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }
    }

    private async _selectFile() {
        const file = await selectFile();
        if (file) {
            await this._uploadFile(file);
        }
    }

    private _selectPhoto() {
        const input = document.createElement("input");
        input.type = "file";
        input.accept = "image/*";
        input.capture = "environment";
        input.style.display = "none";
        document.body.appendChild(input);
        input.onchange = async () => {
            const file = input.files?.[0];
            if (file) {
                await this._uploadFile(file);
            }
            input.remove();
        };
        input.click();
    }

    private async _openDocument(document: DocumentDTO) {
        const edited = await this._documentDialog.show(document);
        if (edited) {
            // Reload documents in the background
            void this._load();
        }
    }

    static styles = [
        shared,
        css`
            :host {
                position: relative;
            }

            .documents-grid {
                display: grid;
                grid-template-columns: repeat(auto-fill, minmax(15em, 1fr));
                grid-gap: 0.5em;
            }
        `,
    ];

    render() {
        return this.expanded ? this._renderVertical() : this._renderHorizontal();
    }

    private _renderHorizontal() {
        return html`
            ${!this._documents.length
                ? html`
                      <div class="stretch centering vertical layout">
                          <ptc-ui-icon name="icon.file.collection" class="giant faded"></ptc-ui-icon>
                          <div>Es liegen noch keine Dokumente für Sie vor.</div>
                      </div>
                  `
                : html`
                      <div class="horizontal scroller" style="margin-top: -1em">
                          <div class="margined horizontal layout">
                              ${this._months.map(
                                  ({ date, documents }) => html`
                                      <div>
                                          <div
                                              class="small padded sticky-left semibold blue colored-text"
                                              style="display: inline-block"
                                          >
                                              ${formatMonth(date)}
                                          </div>
                                          <div class="horizontal spacing layout">
                                              ${documents.map(
                                                  (document) => html`
                                                      <div
                                                          class="padded click horizontal center-aligning layout ${document
                                                              .form?.status === FormStatus.InProgress
                                                              ? "shining"
                                                              : ""} rounded"
                                                          style="padding-left: 0;"
                                                          @click=${() => this._openDocument(document)}
                                                      >
                                                          <ptc-ui-icon
                                                              name="${fileIcon(document.type)}"
                                                              class="massive"
                                                          ></ptc-ui-icon>
                                                          <div class="stretch">
                                                              <div class="small semibold ellipsis bottom-margined">
                                                                  ${document.name}
                                                              </div>
                                                              <div class="tiny horizontal spacing layout">
                                                                  <div class="slim pill">
                                                                      <ptc-ui-icon
                                                                          name="icon.object.date"
                                                                      ></ptc-ui-icon>
                                                                      ${formatDate(document.date, false)}
                                                                  </div>
                                                                  ${document.tags.length > 1
                                                                      ? html`
                                                                            <div
                                                                                class="slim pill ellipsis"
                                                                                style="max-width: 100%"
                                                                            >
                                                                                <ptc-ui-icon
                                                                                    name="icon.object.category"
                                                                                ></ptc-ui-icon>
                                                                                ${document.tags.length} Tags
                                                                            </div>
                                                                        `
                                                                      : document.tags.map(
                                                                            (tag) => html`
                                                                                <div
                                                                                    class="slim pill ellipsis"
                                                                                    style="max-width: 100%; --color-highlight: ${tag.color}"
                                                                                >
                                                                                    <ptc-ui-icon
                                                                                        name="icon.object.tag"
                                                                                    ></ptc-ui-icon>
                                                                                    ${tag.name}
                                                                                </div>
                                                                            `
                                                                        )}
                                                                  ${document.form
                                                                      ? html`<div
                                                                            class="pill"
                                                                            style="--color-highlight: ${formStatusColor(
                                                                                document.form.status
                                                                            )}"
                                                                        >
                                                                            <ptc-ui-icon
                                                                                name="icon.object.form"
                                                                            ></ptc-ui-icon>
                                                                            ${formStatusLabel(document.form.status)}
                                                                        </div>`
                                                                      : ""}
                                                              </div>
                                                          </div>
                                                      </div>
                                                  `
                                              )}
                                          </div>
                                      </div>
                                  `
                              )}
                          </div>
                      </div>
                  `}
        `;
    }

    private _renderVertical() {
        return html`
            <div class="fullbleed vertical layout">
                <div class="horizontally-padded bottom-padded horizontal center-aligning layout border-bottom">
                    <div class="small spacing wrapping center-aligning horizontal layout stretch">
                        <button
                            class="slim ${!this._selectedTag ? "primary" : "transparent"}"
                            @click=${() => (this._selectedTag = null)}
                        >
                            <ptc-ui-icon name="icon.object.category"></ptc-ui-icon> Alle
                            <span class="subtle" style="display: inline-block; margin-left: 0.25em;">
                                ${this._documents.length}
                            </span>
                        </button>
                        ${this._availableTags.map((tag) => {
                            const count = this._documents.filter((doc) => doc.tags.some((t) => t.id === tag.id)).length;
                            return count
                                ? html`
                                      <button
                                          class="slim ${this._selectedTag?.id === tag.id ? "primary" : "transparent"}"
                                          style="--color-highlight: ${tag.color}"
                                          @click=${() => (this._selectedTag = tag)}
                                      >
                                          <ptc-ui-icon name="icon.object.tag"></ptc-ui-icon> ${tag.name}
                                          <span class="subtle" style="display: inline-block; margin-left: 0.25em;">
                                              ${count}
                                          </span>
                                      </button>
                                  `
                                : "";
                        })}
                    </div>
                </div>

                ${!this._documents.length
                    ? html`
                          <div class="stretch centering vertical layout">
                              <ptc-ui-icon name="icon.file.collection" class="giant faded"></ptc-ui-icon>
                              <div>Es liegen noch keine Dokumente für Sie vor.</div>
                          </div>
                      `
                    : html`
                          <ptc-scroller class="stretch">
                              ${this._months.map(({ date, documents }) => {
                                  documents = documents.filter(
                                      (doc) =>
                                          !this._selectedTag || doc.tags.some((tag) => tag.id === this._selectedTag?.id)
                                  );
                                  return !documents.length
                                      ? null
                                      : html`
                                            <div
                                                class="small padded border-bottom subtle semibold sticky"
                                                style="background: var(--color-background)"
                                            >
                                                ${formatMonth(date)}
                                            </div>
                                            ${documents
                                                .filter(
                                                    (doc) =>
                                                        !this._selectedTag ||
                                                        doc.tags.some((tag) => tag.id === this._selectedTag?.id)
                                                )
                                                .map(
                                                    (document) => html`
                                                        <div
                                                            class="padded click horizontal center-aligning layout border-bottom"
                                                            style="padding-left: 0;"
                                                            @click=${() => this._openDocument(document)}
                                                        >
                                                            <ptc-ui-icon
                                                                name="${fileIcon(document.type)}"
                                                                class="massive"
                                                            ></ptc-ui-icon>
                                                            <div class="stretch collapse">
                                                                <div class="small semibold ellipsis bottom-margined">
                                                                    ${document.name}
                                                                </div>
                                                                <div class="tiny horizontal spacing layout">
                                                                    <div class="slim pill">
                                                                        <ptc-ui-icon
                                                                            name="icon.object.date"
                                                                        ></ptc-ui-icon>
                                                                        ${formatDate(document.date, false)}
                                                                    </div>
                                                                    ${document.tags.map(
                                                                        (tag) => html`
                                                                            <div
                                                                                class="slim pill ellipsis"
                                                                                style="max-width: 100%; --color-highlight: ${tag.color}"
                                                                            >
                                                                                <ptc-ui-icon
                                                                                    name="icon.object.tag"
                                                                                ></ptc-ui-icon>
                                                                                ${tag.name}
                                                                            </div>
                                                                        `
                                                                    )}
                                                                    ${document.form
                                                                        ? html`<div
                                                                              class="pill"
                                                                              style="--color-highlight: ${formStatusColor(
                                                                                  document.form.status
                                                                              )}"
                                                                          >
                                                                              <ptc-ui-icon
                                                                                  name="icon.object.form"
                                                                              ></ptc-ui-icon>
                                                                              ${formStatusLabel(document.form.status)}
                                                                          </div>`
                                                                        : ""}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    `
                                                )}
                                        `;
                              })}
                          </ptc-scroller>
                      `}
                ${this._enabledFeatures?.staffDocumentUpload && app.hasPermission("staff.documents.upload")
                    ? html`
                          <div class="double-margined spacing evenly stretching horizontal layout hide-when-collapsed">
                              <button type="button" class="ghost" @click=${() => void this._selectFile()}>
                                  <ptc-ui-icon name="icon.action.upload"></ptc-ui-icon>
                              </button>
                              <button type="button" class="ghost" @click=${() => this._selectPhoto()}>
                                  <ptc-ui-icon name="icon.action.take-photo"></ptc-ui-icon>
                              </button>
                          </div>
                      `
                    : ""}
            </div>

            <div class="fullbleed center-aligning center-justifying vertical layout scrim" ?hidden=${!this._loading}>
                <ptc-ui-spinner ?active=${this._loading}></ptc-ui-spinner>
            </div>
        `;
    }
}
